import React, { useMemo } from 'react';
import {
  useAppStateAssetReportListStore,
  useAppStateCurrentAssetReportStore,
  useAppStateInvestmentIncomeListStore,
  useAppStateManageAssetReportModalDataStore,
  useAppStateOwnerFilterStore,
  useAppStatePrevAssetReportStore,
} from '../../../common/store/AppStateStore.zustand';
import { getAssetInvestmentTotalAmount } from '../util/AssetStatusCard.util';
import { numberWithCommas } from '../../../common/util/NumberUtils';
import {
  getMonthlyInvestmentPerformanceAmount,
  getTotalInvestmentPerformanceAmount,
  getYearToMonthInvestmentPerformanceAmount,
} from '../util/InvestmentPerformanceCard.util';

const InvestmentPerformanceCard = () => {
  const { currentAssetReport } = useAppStateCurrentAssetReportStore();
  const { prevAssetReport } = useAppStatePrevAssetReportStore();
  const { assetReportList } = useAppStateAssetReportListStore();
  const { investmentIncomeList } = useAppStateInvestmentIncomeListStore();
  const { setManageAssetReportModalData } = useAppStateManageAssetReportModalDataStore();
  const { ownerFilter } = useAppStateOwnerFilterStore();

  const investmentData = useMemo(() => {
    if (!currentAssetReport) return null;

    const filteredItemList = ownerFilter
      ? currentAssetReport.reportItemList.filter((item) => item.owner === ownerFilter)
      : currentAssetReport.reportItemList;

    const totalInvestmentAmount = getAssetInvestmentTotalAmount(filteredItemList);
    return {
      totalInvestmentAmount,
      monthInvestmentIncome: getMonthlyInvestmentPerformanceAmount(
        currentAssetReport,
        prevAssetReport,
        ownerFilter,
        investmentIncomeList,
      ),
      yearInvestmentIncome: getYearToMonthInvestmentPerformanceAmount(
        assetReportList,
        currentAssetReport.yearMonth,
        ownerFilter,
        investmentIncomeList,
      ),
      totalInvestmentIncome: getTotalInvestmentPerformanceAmount(
        currentAssetReport,
        assetReportList,
        ownerFilter,
        investmentIncomeList,
      ),
    };
  }, [assetReportList, currentAssetReport, investmentIncomeList, ownerFilter, prevAssetReport]);

  const handleAssetReportClick = () => {
    setManageAssetReportModalData({ isOpen: true, onClose: () => setManageAssetReportModalData(undefined) });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300 group relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/5 rounded-full -translate-y-32 translate-x-32 group-hover:translate-y-[-8rem] transition-transform duration-500"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-600/5 rounded-full translate-y-32 -translate-x-32 group-hover:translate-y-[8rem] transition-transform duration-500"></div>
      
      <div className="flex justify-between items-center mb-6 relative">
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-1 flex items-center">
            <span>투자 성과</span>
          </h3>
          <p className="text-sm text-gray-500">투자금액과 투자수익 현황</p>
        </div>
      </div>

      {!investmentData || investmentData.totalInvestmentAmount === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 bg-gray-50/50 rounded-lg p-6">
          <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-chart-line text-3xl text-purple-300"></i>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">투자 데이터가 없습니다</h4>
          <p className="text-sm text-gray-500 text-center max-w-sm">
            투자 자산을 추가하여 투자 성과를 확인해보세요.
          </p>
          <button
            className="mt-6 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center space-x-2 !rounded-button whitespace-nowrap"
            onClick={handleAssetReportClick}
          >
            <i className="fas fa-plus"></i>
            <span>투자 자산 추가하기</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <i className="fas fa-coins text-purple-600"></i>
              <span className="text-sm text-purple-700 font-medium">총 투자금액</span>
            </div>
            <div className="text-2xl font-bold text-purple-900">
              {numberWithCommas(investmentData.totalInvestmentAmount)}원
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <i className="fas fa-calendar-day text-blue-600"></i>
              <span className="text-sm text-blue-700 font-medium">한달 투자수익</span>
            </div>
            <div className="text-2xl font-bold text-blue-900">
              {numberWithCommas(investmentData.monthInvestmentIncome)}원
            </div>
          </div>

          <div className="bg-emerald-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <i className="fas fa-calendar-alt text-emerald-600"></i>
              <span className="text-sm text-emerald-700 font-medium">올해 투자수익</span>
            </div>
            <div className="text-2xl font-bold text-emerald-900">
              {numberWithCommas(investmentData.yearInvestmentIncome)}원
            </div>
          </div>

          <div className="bg-amber-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <i className="fas fa-chart-line text-amber-600"></i>
              <span className="text-sm text-amber-700 font-medium">총 누적 투자수익</span>
            </div>
            <div className="text-2xl font-bold text-amber-900">
              {numberWithCommas(investmentData.totalInvestmentIncome)}원
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentPerformanceCard;
